/**
 * U10 slice A — DD-19 unified-datasource core (build-order:144; BACKLOG DD-19
 * row, created at the U10 scope lock).
 *
 * Shared translation core for the AG Grid Infinite Row Model (IRM)
 * datasources. Every IRM page hand-rolls the same three seams around its
 * bespoke query:
 *
 *   1. sortModel → server ORDER BY, gated by a page-owned colId allowlist
 *      (with an optional synthetic-colId remap, e.g. BuyingSession's
 *      days_locked → days_credit_locked_safety/_velocity switch) and a
 *      page-owned fallback sort;
 *   2. AG Grid's half-open [startRow, endRow) window → PostgREST's inclusive
 *      .range bounds (the −1 lives here, once);
 *   3. fetch result/error → successCallback/failCallback, with
 *      reportDatasourceFailure as the observability sink.
 *
 * DD-19 hoists exactly those three. Predicates, projections, enrichment
 * fetches, and row mapping stay page-owned (the ruled core-as-library shape:
 * pages keep bespoke predicates). The bespoke datasources converge onto this
 * module as the A-sweep reaches their pages — BuyingSession first.
 */
import type { IGetRowsParams } from 'ag-grid-community';
import { reportDatasourceFailure, type DatasourceFailureContext } from './agGridErrorTracker';

export type IrmSortModel = IGetRowsParams['sortModel'];

export interface IrmSortSpec {
  /** Columns allowed to reach server ORDER BY; checked AFTER remap. */
  allowed: ReadonlySet<string>;
  /** Optional synthetic-colId → DB-column remap, applied before the allowlist check. */
  remap?: (colId: string) => string;
  /** Sort applied when no sortModel entry survives the allowlist. */
  fallback: { column: string; ascending: boolean; nullsFirst?: boolean };
  /**
   * nullsFirst for a surviving sortModel column. Omit to keep the query
   * builder's own default — pages that never passed the option before keep
   * byte-identical ORDER BY behavior.
   */
  nullsFirst?: boolean;
}

/** Structural slice of a PostgREST filter builder the sort helper needs. */
export interface IrmOrderable {
  order(column: string, options: { ascending: boolean; nullsFirst?: boolean }): unknown;
}

/**
 * Resolve sortModel[0] through the remap + allowlist. Returns null when
 * nothing survives (caller's fallback applies). Only the first sortModel
 * entry is honored — the IRM fleet is single-sort by convention.
 */
export function resolveIrmSortColumn(
  sortModel: IrmSortModel,
  spec: Pick<IrmSortSpec, 'allowed' | 'remap'>
): { column: string; ascending: boolean } | null {
  if (!sortModel || sortModel.length === 0) return null;
  const first = sortModel[0];
  const column = spec.remap ? spec.remap(first.colId) : first.colId;
  if (!spec.allowed.has(column)) return null;
  return { column, ascending: first.sort === 'asc' };
}

/** Apply the resolved (or fallback) sort to the page's query. */
export function applyIrmSort<Q extends IrmOrderable>(
  query: Q,
  sortModel: IrmSortModel,
  spec: IrmSortSpec
): Q {
  const resolved = resolveIrmSortColumn(sortModel, spec);
  const column = resolved ? resolved.column : spec.fallback.column;
  const ascending = resolved ? resolved.ascending : spec.fallback.ascending;
  const nullsFirst = resolved ? spec.nullsFirst : spec.fallback.nullsFirst;
  const options: { ascending: boolean; nullsFirst?: boolean } = { ascending };
  if (nullsFirst !== undefined) options.nullsFirst = nullsFirst;
  return query.order(column, options) as Q;
}

/** AG Grid [startRow, endRow) → PostgREST inclusive .range bounds. */
export function irmRangeBounds(startRow: number, endRow: number): { from: number; to: number } {
  return { from: startRow, to: endRow - 1 };
}

/**
 * The IRM getRows contract: run the page's bespoke fetch for the requested
 * window, deliver rows + the exact filtered total to successCallback, and
 * route any throw through reportDatasourceFailure → failCallback.
 */
export async function runIrmGetRows<RowT>(opts: {
  params: IGetRowsParams;
  /** Page label for the failure tracker. */
  page: string;
  failureContext: DatasourceFailureContext;
  fetch: (window: {
    startRow: number;
    endRow: number;
    sortModel: IrmSortModel;
  }) => Promise<{ rows: RowT[]; total: number }>;
}): Promise<void> {
  const { startRow, endRow, sortModel, successCallback, failCallback } = opts.params;
  try {
    const { rows, total } = await opts.fetch({ startRow, endRow, sortModel });
    successCallback(rows, total);
  } catch (e) {
    reportDatasourceFailure(opts.page, opts.failureContext, e, () => failCallback());
  }
}
